import React from 'react'
import { OmButton, OmDropdownMenu } from '@owma/kit'
import classNames from 'classnames'
import dayjs from 'dayjs'
import settings from 'popups/Settings'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './style.module.scss'

import imgAccessoryDue from 'assets/images/todo/icon-due.svg'
import imgIcon from 'assets/images/todo/icon-reminder.svg'
import { FilterType } from '../../../modules/TodoFilter'
import { getSortedFolders } from '../../../modules/TodoFolderSync'
import CalendarDatePicker from '../../DatePicker'
import { DatePickerType } from '../../DatePicker/DatePickerType'
import { getDefaultDueDate } from '../../TodoEditor'
import { calculateReminderDateTime, createReminderOptions } from '../../TodoEditor/ReminderSelector'

import imgRecurrence from 'assets/images/calendar/recurrence-16.svg'
import RecurrenceEditor from 'pages/calendar/components/EventEditor/RecurrenceEditor'
import { getReminderTimeStr } from '../../DateFormatter'
import useDueDateRecurrence from '../../hooks/useDueDateRecurrence'

interface QuickAddTaskEditorProps {
  defaultFolder?: any
  currentFilter?: string
  onAdd: (taskData: any) => void
  onCancel?: () => void
  placeholder?: string
}

interface QuickAddTaskEditorHandle {
  clearAll: () => void
}

/**
 * FilterType 의 filter 에 따라 QuickAddTask 진입 시 default 설정값이 달라진다.
 * All : Default 설정값 없음
 * Today : Due date 를 오늘로 자동 지정
 * Important : Priority 를 High 로 자동 지정
 * Planned : Due date 를 오늘로 자동 지정
 */
const QuickAddTaskEditor = forwardRef<QuickAddTaskEditorHandle, QuickAddTaskEditorProps>(
  (
    {
      defaultFolder,
      currentFilter, // filter 에 따라 설정값 변경이 있다.
      onAdd,
      onCancel,
      placeholder = 'todo.todolist.quickAddTask',
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [value, setValue] = useState('')
    const [dueDate, setDueDate] = useState<Date | null>(null)
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [alarms, setAlarms] = useState<any[]>([])
    const [reminderDropdownOpen, setReminderDropdownOpen] = useState(false)
    const [reminderDatePickerOpen, setReminderDatePickerOpen] = useState(false)
    const [priority, setPriority] = useState<string>('MEDIUM')
    const inputRef = useRef<HTMLInputElement>(null)
    //  isSubmitting 상태 추가하여 중복 제출 방지
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [recurrenceRule, setRecurrenceRule] = useState<any>(null)

    // Use the common hook for due date and recurrence functionality
    const {
      datePickerOpen,
      dueDateDropdownOpen,
      setDueDateDropdownOpen,
      showRecurrenceEditor,
      setShowRecurrenceEditor,
      getDueDateInfoText,
      handleDateChange,
      handleDatePickerClose,
      handleCustomRecurrenceSave,
      createDueDateDropdownMenuItems,
    } = useDueDateRecurrence({
      dueDate: dueDate,
      onDueDateChange: (date: Date | null) => {
        setDueDate(date)
        // Clear recurrence when manually picking a date
        if (recurrenceRule) {
          setRecurrenceRule(null)
        }
      },
      onRecurrenceChange: setRecurrenceRule,
    })
    const todoSettings = settings.getValue('todo') || {}
    const defaultDueDateSetting = todoSettings.defaultDueDate || 'no-date'
    // const defaultReminderStartTime = todoSettings.defaultReminderStartTime || 540 // 9AM

    // defaultFolder가 없으면 getSortedFolders의 첫 번째 폴더 사용
    const actualDefaultFolder = defaultFolder || getSortedFolders()[0] || null

    const folderColor =
      actualDefaultFolder?.calendarColor?._cdata || actualDefaultFolder?.color || '#fd4c45' // DEFAULT_FOLDER_COLOR

    // Use ref to track previous dueDate to avoid infinite re-renders
    const prevDueDateRef = useRef<Date | null>(null)

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }

      // filter 에 따라 quickAddTask 의 default 값 설정
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      switch (currentFilter) {
        case FilterType.Today:
          // Set due date to today
          setDueDate(new Date(today))
          break
        case FilterType.Important:
          // Set priority to High and apply defaultDueDate setting
          setPriority('HIGH')
          setDueDate(getDefaultDueDate(defaultDueDateSetting))
          break
        case FilterType.Planned:
          // Set due date to today
          setDueDate(new Date(today))
          break
        default:
          // Apply defaultDueDate setting for All filter
          setDueDate(getDefaultDueDate(defaultDueDateSetting))
          break
      }
    }, [currentFilter])

    // Auto-set reminder when due date changes based on settings
    useEffect(() => {
      // Compare Date objects properly
      const dueDateTime = dueDate ? dueDate.getTime() : null
      const prevDueDateTime = prevDueDateRef.current ? prevDueDateRef.current.getTime() : null
      const dueDateChanged = prevDueDateTime ? dueDateTime !== prevDueDateTime : false

      // Auto-set reminder if:
      // 1. dueDate exists
      // 2. dueDate has actually changed (including from null to a date)
      if (dueDate && dueDateChanged) {
        // Get settings with proper defaults
        const defaultReminderTimeForFuture = todoSettings.defaultReminderTimeForFuture || 'dueDate'
        const defaultReminderStartTime = todoSettings.defaultReminderStartTime || 540 // 9AM (540 minutes)
        const defaultReminderTimeForToday = todoSettings.defaultReminderTimeForToday || 0

        console.log('🔔 QuickAddTaskEditor: Auto-setting reminder for dueDate change!')
        console.log('  - dueDate:', dueDate)
        console.log('  - defaultReminderTimeForFuture:', defaultReminderTimeForFuture)
        console.log('  - defaultReminderStartTime:', defaultReminderStartTime)
        console.log('  - defaultReminderTimeForToday:', defaultReminderTimeForToday)

        // Calculate the reminder date-time based on settings
        const reminderDateTime = calculateReminderDateTime(
          dueDate,
          defaultReminderTimeForFuture,
          defaultReminderStartTime,
          defaultReminderTimeForToday,
        )

        if (reminderDateTime) {
          console.log('Calculated reminder date-time:', reminderDateTime)

          // Set the reminder directly
          const formattedTime = dayjs(reminderDateTime).utc().format('YYYYMMDD[T]HHmmss[Z]')
          console.log('reminder time:', formattedTime)

          const newAlarm = {
            action: 'DISPLAY',
            trigger: {
              params: {},
              val: formattedTime,
            },
          }

          setAlarms([newAlarm])

          // Update start date - reminder 시간이 설정되면 startDate를 업데이트
          const shouldUpdateStart =
            !startDate ||
            dayjs(startDate).format('YYYY-MM-DD HH:mm') !==
              dayjs(reminderDateTime).format('YYYY-MM-DD HH:mm')

          if (shouldUpdateStart) {
            console.log('  📍 Updating startDate to:', reminderDateTime)
            setStartDate(reminderDateTime)
          }
        }
      }

      // Update previous dueDate reference
      prevDueDateRef.current = dueDate
    }, [dueDate, todoSettings, alarms, startDate])

    /**
     * forwardRef와 useImperativeHandle은 부모 컴포넌트가 자식 컴포넌트 내부의 DOM이나 함수에 직접 접근할 수 있게 해주는 도구
     * useImperativeHandle
     * - 전달받은 ref에 직접 DOM이 아닌, "원하는 기능만" 노출시킬 수 있는 Hook
     *   ref.current = { ... }의 형태로 외부에 기능을 제한적으로 제공
     * Expose clearAll method to parent component
     */
    useImperativeHandle(ref, () => ({
      clearAll,
    }))

    const handleSave = () => {
      if (value.trim() && !isSubmitting) {
        setIsSubmitting(true)
        const taskData = {
          summary: value.trim(),
          dueDate: dueDate,
          startDate: startDate,
          alarms: alarms,
          priority: priority,
          folder: actualDefaultFolder,
          recurrenceRule: recurrenceRule, // recurrence 추가
        }
        onAdd(taskData)
        setTimeout(() => {
          clearAll()
          setIsSubmitting(false)
        }, 100)
      }
    }

    // 취소 버튼 클릭 시 호출 (지금은 미사용)
    const _handleCancel = () => {
      clearAll()
      if (onCancel) onCancel()
    }

    const clearAll = () => {
      setValue('')
      setStartDate(null)
      setAlarms([])
      setRecurrenceRule(null) // recurrence 초기화 추가

      // Reset to filter-specific default values instead of completely clearing
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      switch (currentFilter) {
        case FilterType.Today:
          // Keep due date as today
          setDueDate(new Date(today))
          setPriority('MEDIUM')
          break
        case FilterType.Important:
          // Keep priority as High and apply defaultDueDate setting
          setDueDate(getDefaultDueDate(defaultDueDateSetting))
          setPriority('HIGH')
          break
        case FilterType.Planned:
          // Keep due date as today
          setDueDate(new Date(today))
          setPriority('MEDIUM')
          break
        default:
          // Apply defaultDueDate setting for All filter
          setDueDate(getDefaultDueDate(defaultDueDateSetting))
          setPriority('MEDIUM')
          break
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        handleSave()
      }
      // else if (e.key === 'Escape') {
      //   handleCancel()
      // } else if (e.key === 'Backspace' && !value) {
      //   handleCancel()
      // }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value)
    }

    const handleDueDateChange = (date: Date | null) => {
      handleDateChange(date)
      setDueDateDropdownOpen(false)
    }

    const handleReminderChange = (value: any, isDateTime: boolean = false) => {
      if (value === null) {
        setAlarms([])
      } else if (isDateTime) {
        // 절대 시간 알람 설정
        let reminderDateTime
        let formattedTime

        if (value instanceof Date) {
          // Date 객체인 경우
          reminderDateTime = dayjs(value)
          formattedTime = reminderDateTime.utc().format('YYYYMMDD[T]HHmmss[Z]')
        } else {
          console.warn('Unsupported value type for reminder:', typeof value, value)
          // const isoString = typeof value === 'string' ? value : new Date(value).toISOString()
          // reminderDateTime = new Date(isoString)
          // formattedTime = isoString
          return
        }

        // reminder 시간이 설정되면 startDate를 업데이트
        if (reminderDateTime && reminderDateTime.isValid()) {
          // 로컬 타임존으로 변환된 시간을 사용 (초는 0으로 설정)
          let targetDateTime = reminderDateTime.second(0)

          // UTC 시간을 로컬 타임존으로 변환
          if (targetDateTime.isUTC()) {
            targetDateTime = targetDateTime.local()
          }

          // startDate를 reminder 시간으로 설정
          const shouldUpdateStart =
            !startDate ||
            dayjs(startDate).format('YYYY-MM-DD HH:mm') !==
              targetDateTime.format('YYYY-MM-DD HH:mm')

          if (shouldUpdateStart) {
            // 로컬 타임존의 Date 객체로 변환
            setStartDate(targetDateTime.toDate())
          }
        }

        setAlarms([
          {
            action: 'DISPLAY',
            trigger: {
              params: {},
              val: formattedTime,
            },
          },
        ])
      } else {
        // 상대 시간 알람 설정
        setAlarms([{ action: 'DISPLAY', trigger: value }])
      }
      setReminderDropdownOpen(false)
    }

    const handleReminderPickDateClick = () => {
      setReminderDropdownOpen(false)
      setReminderDatePickerOpen(true)
    }

    const handleReminderDateTimeChange = (dateTime: Date | null) => {
      handleReminderChange(dateTime, true)
      setReminderDatePickerOpen(false)
    }

    const handleReminderDatePickerClose = () => {
      setReminderDatePickerOpen(false)
    }

    // Due date 표시 텍스트 생성
    // const getDueDateDisplayText = () => {
    //   // if (recurrenceRule) {
    //   //   return getCurrentRecurrenceDescription(recurrenceRule, t)
    //   // }
    //   if (!dueDate) return null
    //   return formatDueDate(dueDate, t)
    // }

    // Reminder 표시 텍스트 생성
    const getReminderDisplayText = () => {
      if (alarms.length === 0) return null

      const reminderTime = getReminderTimeStr(alarms[0], dueDate, null, t)
      return reminderTime
    }

    // Due date dropdown items from hook
    const dueDateDropdownItems = createDueDateDropdownMenuItems(styles, true)

    const hasExistingReminder = alarms.length > 0
    const reminderDropdownItems = createReminderOptions(t, hasExistingReminder, true).map(
      (option: any) => ({
        key: option.key,
        label: (
          <div className={styles.menuItem}>
            <div className={styles.menuItemContent}>
              <div className={styles.reminderInfo}>
                <div className={styles.reminderLabel}>{option.label}</div>
              </div>
            </div>
          </div>
        ),
        onClick: () => {
          if (option.key === 'pick_date') {
            handleReminderPickDateClick()
          } else if (option.key === 'off') {
            handleReminderChange(null, false)
          } else {
            handleReminderChange(option.value, option.isDateTime)
          }
        },
      }),
    )

    const dueDateDisplayText = getDueDateInfoText()

    // dueDateDisplayText is already available from the hook
    const reminderDisplayText = getReminderDisplayText()

    return (
      <div className={styles.quickAddItemEditor}>
        <div className={styles.container}>
          {/* Completed checkbox */}
          <div className={styles.checkBoxArea}>
            <div className={styles.checkContainer}>
              <div className={styles.checkbox} style={{ borderColor: folderColor || '#fd4c45' }} />
            </div>
          </div>

          {/* task summary */}
          <div className={styles.editArea}>
            {/* input summart */}
            <div className={styles.editField}>
              <input
                ref={inputRef}
                value={value}
                maxLength={100}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={t(placeholder) /* i18next-extract-disable-line */}
                className={styles.titleInput}
              />
            </div>

            {/* due, reminder selectors */}
            <div className={styles.details}>
              {/* Due date selector dropdown 
              dueDate 변경 시 호출 순서
              1. dropdown의 "Today", "Tomorrow" 등을 선택
              2. handleQuickDateSelect → onDueDateChange → setDueDate 호출
              3. useDueDateRecurrence 훅의 dueDateDisplayText useEffect가 실행되어 새로운 표시 텍스트 계산
              4. getDueDateInfoText()가 포맷된 날짜 문자열 반환
              5. due date가 표시.
              */}
              <OmDropdownMenu
                menu={{ items: dueDateDropdownItems as any }}
                trigger={['click']}
                open={dueDateDropdownOpen}
                onOpenChange={setDueDateDropdownOpen}
                placement="bottomLeft"
                overlayClassName={classNames('contextMenu', styles.datePickerDropdown)}
                getPopupContainer={() => document.body}
              >
                <div
                  className={`${dueDateDisplayText ? styles.selectorActive : styles.selectorNone}`}
                >
                  <img src={imgAccessoryDue} alt="due" className={styles.icon} />
                  {dueDateDisplayText && (
                    <span className={styles.dueText}>{dueDateDisplayText}</span>
                  )}
                </div>
              </OmDropdownMenu>

              {/* Reminder selector dropdown */}
              <OmDropdownMenu
                menu={{ items: reminderDropdownItems }}
                trigger={['click']}
                open={reminderDropdownOpen}
                onOpenChange={setReminderDropdownOpen}
                placement="bottomLeft"
                overlayClassName="contextMenu"
                getPopupContainer={() => document.body}
              >
                <div
                  className={`${reminderDisplayText ? styles.selectorActive : styles.selectorNone}`}
                >
                  <img src={imgIcon} alt="reminder" className={styles.icon} />
                  {reminderDisplayText && (
                    <span className={styles.dueText}>{reminderDisplayText}</span>
                  )}
                </div>
              </OmDropdownMenu>

              {/* Recurrence icon */}
              {recurrenceRule && (
                <>
                  <div className={styles.recurrenceIcon}>
                    <img src={imgRecurrence} alt="recurrence" className={styles.iconSmall} />
                  </div>

                  {/* <span className={styles.dueText}>
                    {getCurrentRecurrenceDescription(recurrenceRule, t)}
                  </span> */}
                </>
              )}
            </div>
          </div>

          {/* add a task button */}
          <div className={classNames(styles.addButtonArea, 'type2')}>
            <OmButton
              variant="default"
              onClick={handleSave}
              className={styles.addButton}
              disabled={!value.trim() || isSubmitting}
            >
              {t('todo.todolist.add')}
            </OmButton>
          </div>
        </div>

        {/* Custom Date Picker */}
        {datePickerOpen && (
          <div className={styles.datePickerOverlay} onClick={handleDatePickerClose}>
            <div onClick={(e) => e.stopPropagation()}>
              <CalendarDatePicker
                value={dueDate}
                onChange={handleDueDateChange}
                onClose={handleDatePickerClose}
              />
            </div>
          </div>
        )}

        {/* Reminder Date Time Picker */}
        {reminderDatePickerOpen && (
          <div className={styles.datePickerOverlay} onClick={handleReminderDatePickerClose}>
            <div onClick={(e) => e.stopPropagation()}>
              <CalendarDatePicker
                pickerType={DatePickerType.DATETIME}
                value={startDate ? startDate : dueDate ? dueDate : new Date()}
                onChange={handleReminderDateTimeChange}
                onClose={handleReminderDatePickerClose}
              />
            </div>
          </div>
        )}

        {/* Recurrence Editor */}
        {showRecurrenceEditor && (
          <RecurrenceEditor
            open={showRecurrenceEditor}
            onClose={() => setShowRecurrenceEditor(false)}
            onSave={handleCustomRecurrenceSave as any}
            startDate={dueDate ? new Date(dueDate) : new Date()}
            options={recurrenceRule?.options || null}
          />
        )}
      </div>
    )
  },
)

export default QuickAddTaskEditor
